import { apiClient } from './apiClient';
import { resolveAvatarUrl } from '../constants/assets';

export type TrialStatus = 'PENDING' | 'ACCEPTED' | 'REJECTED' | 'SUBMITTED' | 'PASSED' | 'FAILED';
export type TrialPaymentStatus = 'NOT_REQUIRED' | 'UNPAID' | 'PAID';
export type TrialViewMode = 'client' | 'creator';

export interface Trial {
    id: string;
    briefId: string;
    briefTitle: string;
    clientUserId: string;
    creatorUserId: string;
    clientName: string;
    clientAvatar: string;
    creatorName: string;
    creatorAvatar: string;
    status: TrialStatus;
    budgetRange: string;
    createdAt: number;
    updatedAt?: number;
    description?: string;
    tags?: string[];
    deliveryGoals?: string;
    clientRemark?: string;
    notes?: string;
    submissionUrls?: string[];
    feedback?: string;
    initiatedByUserId?: string;
    trialFeeAmount?: number;
    paymentStatus: TrialPaymentStatus;
    paidAt?: number;
}

export interface GetTrialsResponse {
    trials: Trial[];
    totalCount: number;
}

const normalizeTrial = (trial: Trial): Trial => ({
    ...trial,
    clientAvatar: resolveAvatarUrl(trial.clientAvatar),
    creatorAvatar: resolveAvatarUrl(trial.creatorAvatar)
});

export const trialService = {
    getTrials: async (params?: { status?: string; limit?: number; viewMode?: TrialViewMode }) => {
        const queryParams = new URLSearchParams();
        if (params?.status) queryParams.set('status', params.status);
        if (params?.viewMode) queryParams.set('viewMode', params.viewMode);
        if (params?.limit) queryParams.set('limit', String(params.limit));
        const query = queryParams.toString();
        const response = await apiClient.get<GetTrialsResponse>(`/api/v1/trials${query ? `?${query}` : ''}`);
        if (response.success && response.data?.trials) {
            return {
                ...response,
                data: {
                    ...response.data,
                    trials: response.data.trials.map(normalizeTrial)
                }
            };
        }
        return response;
    },

    getTrialById: async (trialId: string) => {
        const response = await apiClient.get<{ trial: Trial }>(`/api/v1/trials/${encodeURIComponent(trialId)}`);
        if (response.success && response.data?.trial) {
            return {
                ...response,
                data: {
                    ...response.data,
                    trial: normalizeTrial(response.data.trial)
                }
            };
        }
        return response;
    },

    createTrial: async (trialData: {
        trialId?: string;
        briefId: string;
        clientUserId: string;
        creatorUserId: string;
        initiatedByUserId?: string;
        trialFeeAmount?: number;
        reviewSlaHours?: number;
    }) => {
        return apiClient.post<{ trial: Trial }>('/api/v1/trials', trialData);
    },

    payTrial: async (trialId: string) => {
        return apiClient.post<{ trial: Trial }>(`/api/v1/trials/${encodeURIComponent(trialId)}/pay`, {});
    },

    submitTrialWork: async (trialId: string, submissionUrls: string[]) => {
        return apiClient.post<{ trial: Trial }>(`/api/v1/trials/${encodeURIComponent(trialId)}/submissions`, {
            submissionUrls
        });
    },

    updateTrialBriefing: async (
        trialId: string,
        data: {
            deliveryGoals?: string;
            clientRemark?: string;
        }
    ) => {
        return apiClient.put<{ trial: Trial }>(`/api/v1/trials/${encodeURIComponent(trialId)}/briefing`, data);
    },

    evaluateTrial: async (trialId: string, status: 'PASSED' | 'FAILED', feedback?: string) => {
        return apiClient.post<{ trial: Trial }>(`/api/v1/trials/${encodeURIComponent(trialId)}/evaluate`, {
            status,
            feedback
        });
    },

    deleteTrial: async (trialId: string) => {
        return apiClient.delete<{ success: boolean }>(`/api/v1/trials/${encodeURIComponent(trialId)}`);
    }
};
